import { Box, Divider, Stack, Typography } from "@mui/material";
import { StringField } from "../ConfigFields/StringField";
import { NumberField } from "../ConfigFields/NumberField";
import { EnumField } from "../ConfigFields/EnumField";
import AIModelPicker from "../../../AI/AIModelPicker";
import AIConnectionHealthBanner from "../../../AI/AIConnectionHealthBanner";

// NumberField shows its schema description as a placeholder, which disappears the
// moment the field holds a value — and these all ship with defaults, so the guidance
// would never actually be seen. Rendered as persistent captions instead.
const FieldHint = ({ children }) => (
  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
    {children}
  </Typography>
);

const ProductDiscoveryPreset = ({ schema, config, handleConfigChange }) => {
  const mode = config["mode"] || "send_catalog";

  return (
    <Stack spacing={3}>
      <EnumField
        label="Mode"
        schema={schema?.properties["mode"] || {}}
        value={mode}
        onChange={(val) => handleConfigChange("mode", val || "send_catalog")}
      />

      <StringField
        fieldKey="catalog_id"
        schema={schema?.properties["catalog_id"] || {}}
        value={config["catalog_id"]}
        required={false}
        onChange={(val) => handleConfigChange("catalog_id", val)}
      />

      {mode === "ai_discovery" && (
        <>
          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              AI model
            </Typography>
            <Stack spacing={1.5}>
              <AIModelPicker
                label="AI model for this node"
                capability="chat"
                allowTenantDefault
                value={config["ai_override"] || null}
                onChange={(val) => handleConfigChange("ai_override", val)}
              />
              <AIConnectionHealthBanner override={config["ai_override"] || null} compact />
              <FieldHint>
                Understands the customer&apos;s message and ranks matching products. Leave on the default AI
                model (Agentic AI → Configuration) unless this flow needs a different model or your own key.
              </FieldHint>
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Asking the customer
            </Typography>
            <Stack spacing={2}>
              <StringField
                fieldKey="query_prompt"
                schema={schema?.properties["query_prompt"] || {}}
                value={config["query_prompt"]}
                required={false}
                onChange={(val) => handleConfigChange("query_prompt", val)}
              />
              <Box>
                <StringField
                  fieldKey="new_search_prompt"
                  schema={schema?.properties["new_search_prompt"] || {}}
                  value={config["new_search_prompt"]}
                  required={false}
                  onChange={(val) => handleConfigChange("new_search_prompt", val)}
                />
                <FieldHint>
                  Sent when the customer taps “New Search” or “Search Again”. Set this
                  even if the query prompt is blank because an earlier message already
                  asks — otherwise those buttons get no reply. Leave blank to send nothing.
                </FieldHint>
              </Box>
              <Box>
                <StringField
                  fieldKey="searching_message"
                  schema={schema?.properties["searching_message"] || {}}
                  value={config["searching_message"]}
                  required={false}
                  onChange={(val) => handleConfigChange("searching_message", val)}
                />
                <FieldHint>
                  Optional. Sent just before the catalog is searched so the customer
                  isn't left waiting in silence — including when “View more items” has
                  to fetch a further set of matches. Leave blank to send nothing.
                </FieldHint>
              </Box>
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Showing results
            </Typography>
            <Stack spacing={2}>
              <StringField
                fieldKey="section_title"
                schema={schema?.properties["section_title"] || {}}
                value={config["section_title"]}
                required={false}
                onChange={(val) => handleConfigChange("section_title", val)}
              />

              <Box>
                <NumberField
                  fieldKey="max_results"
                  schema={schema?.properties["max_results"] || {}}
                  value={config["max_results"]}
                  required={false}
                  onChange={(val) => handleConfigChange("max_results", val)}
                />
                <FieldHint>
                  Products shown per batch — WhatsApp allows at most 30 in one message.
                  This does not cap how many matches the customer can reach: when a
                  search finds more, they get a “View more items” button that sends the
                  next batch.
                </FieldHint>
              </Box>

              <Box>
                <NumberField
                  fieldKey="candidate_pool_size"
                  schema={schema?.properties["candidate_pool_size"] || {}}
                  value={config["candidate_pool_size"]}
                  required={false}
                  onChange={(val) => handleConfigChange("candidate_pool_size", val)}
                />
                <FieldHint>
                  How many catalog items are fetched and ranked by AI per search
                  (30–200). Higher finds more for broad searches, but every search costs
                  more AI tokens and takes longer — including the many where the customer
                  only looks at the first batch. 150 suits most catalogs. If a customer
                  pages past everything ranked here, the next set is fetched
                  automatically, so this is not a limit on how many products they can
                  see.
                </FieldHint>
              </Box>
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Staying on topic
            </Typography>
            <Stack spacing={2}>
              <Box>
                <StringField
                  fieldKey="off_topic_message"
                  schema={schema?.properties["off_topic_message"] || {}}
                  value={config["off_topic_message"]}
                  required={false}
                  onChange={(val) => handleConfigChange("off_topic_message", val)}
                />
                <FieldHint>
                  Sent when the customer asks about something this section can't help
                  with, alongside a “Back to main menu” option that restarts the flow
                  from the beginning.
                </FieldHint>
              </Box>
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              When they don't buy
            </Typography>
            <Stack spacing={2}>
              <Box>
                <NumberField
                  fieldKey="max_queries"
                  schema={schema?.properties["max_queries"] || {}}
                  value={config["max_queries"]}
                  required={false}
                  onChange={(val) => handleConfigChange("max_queries", val)}
                />
                <FieldHint>
                  How many searches a customer may run before being pointed at your
                  website instead. Set to 0 for unlimited. Adding anything to the cart
                  moves the order on regardless, and messages that aren't product
                  searches don't count towards this.
                </FieldHint>
              </Box>

              <StringField
                fieldKey="explore_website_message"
                schema={schema?.properties["explore_website_message"] || {}}
                value={config["explore_website_message"]}
                required={false}
                onChange={(val) => handleConfigChange("explore_website_message", val)}
              />

              <Box>
                <StringField
                  fieldKey="website_url"
                  schema={schema?.properties["website_url"] || {}}
                  value={config["website_url"]}
                  required={false}
                  onChange={(val) => handleConfigChange("website_url", val)}
                />
                <FieldHint>
                  Leave blank to send the message above without a button.
                </FieldHint>
              </Box>

              <StringField
                fieldKey="website_button_text"
                schema={schema?.properties["website_button_text"] || {}}
                value={config["website_button_text"]}
                required={false}
                onChange={(val) => handleConfigChange("website_button_text", val)}
              />
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              When nothing matches
            </Typography>
            <Stack spacing={2}>
              <StringField
                fieldKey="no_results_message"
                schema={schema?.properties["no_results_message"] || {}}
                value={config["no_results_message"]}
                required={false}
                onChange={(val) => handleConfigChange("no_results_message", val)}
              />
              <StringField
                fieldKey="end_of_results_message"
                schema={schema?.properties["end_of_results_message"] || {}}
                value={config["end_of_results_message"]}
                required={false}
                onChange={(val) => handleConfigChange("end_of_results_message", val)}
              />
            </Stack>
          </Box>
        </>
      )}
    </Stack>
  );
};

export default ProductDiscoveryPreset;
